/**
 * useCrmObjectSnapshots — plural variant of useCrmObjectSnapshot.
 *
 * Subscribes to N entity IDs in the CRM object store and returns a
 * Map<entity_id, CrmObject | undefined>. Designed for components (like
 * MeetingDetailSection) that need to observe the status of several entities
 * at once without calling a hook in a loop.
 *
 * Auto-batching: all `ensureEntity` calls for a single render tick are
 * coalesced by entityFetchBatcher into a single GET request. N cards
 * mounting in the same render → 1 HTTP request.
 *
 * WS-driven updates: when a WebSocket event updates one of the subscribed
 * entities (via applyEvent → upsertObject), useSyncExternalStore fires the
 * subscribe callback, getSnapshot returns a new Map, and only the affected
 * component subtree re-renders.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';

import { ensureEntity } from '@/lib/crm/object-cards/entityFetchBatcher';
import { mergeProjection, useActivePreviewHandle } from '@/lib/crm/preview';
import { hasCompleteObject, subscribeToObject } from '@/lib/crm/store';
import type { CrmObject } from '@/types/crm';

// Stable empty Map — returned when input is empty to avoid reference churn.
const EMPTY_MAP = Object.freeze(new Map<string, CrmObject | undefined>());

const NOOP_UNSUBSCRIBE = () => {};

/**
 * Subscribe to multiple CRM objects in the store and return a stable Map.
 *
 * @param entityIds - Entity UUIDs to subscribe to. Deduped + sorted internally
 *   so the caller does not need to stabilize the array reference.
 * @param orgId - Organization ID for triggering fetches. If undefined or <= 0,
 *   subscriptions are created but fetches are skipped.
 * @returns Map<entity_id, CrmObject | undefined>. Value is undefined when the
 *   entity has not yet been fetched/received via WS. The Map reference is
 *   stable across re-renders when no underlying object has changed.
 *
 * @example
 * const researchObjects = useCrmObjectSnapshots(researchEntityIds, orgId);
 * // Map re-renders only the affected row when one research entity flips
 * // from 'in_progress' to 'complete' via a WS event.
 */
export function useCrmObjectSnapshots(
  entityIds: readonly string[],
  orgId: number | undefined
): Map<string, CrmObject | undefined> {
  // Derive a stable string key from the sorted+deduped IDs so the downstream
  // memos don't depend on the array reference (which changes every render).
  const stableKey = useMemo(
    () => [...new Set(entityIds)].sort().join('\0'),
    [entityIds]
  );

  // Sorted, deduped array derived from the stable key.
  const dedupedIds = useMemo(
    () => (stableKey ? stableKey.split('\0') : []),
    [stableKey]
  );

  // Build per-object store subscriptions, one per deduped ID.
  // subscribeToObject is stable for the same timelineId within a store lifetime.
  const subs = useMemo(
    () => dedupedIds.map(id => ({ id, ...subscribeToObject(id) })),
    [dedupedIds]
  );

  // Fan-out subscribe: any per-object change fires the useSyncExternalStore cb.
  const subscribe = useCallback(
    (cb: () => void) => {
      if (subs.length === 0) {
        return NOOP_UNSUBSCRIBE;
      }
      const unsubs = subs.map(s => s.subscribe(cb));
      return () => {
        for (const unsub of unsubs) {
          unsub();
        }
      };
    },
    [subs]
  );

  // Snapshot cache — mutable ref, intentionally mutated inside getSnapshot to
  // implement reference stability without extra re-renders. React's
  // useSyncExternalStore calls getSnapshot multiple times per render cycle; the
  // cache ensures the same Map reference is returned as long as no underlying
  // object changed (preventing the "Maximum update depth exceeded" loop).
  const cacheRef = useRef<{
    subs: typeof subs;
    map: Map<string, CrmObject | undefined>;
    snaps: Map<string, CrmObject | undefined>;
  } | null>(null);

  const getSnapshot = useCallback((): Map<string, CrmObject | undefined> => {
    if (subs.length === 0) {
      return EMPTY_MAP as Map<string, CrmObject | undefined>;
    }

    const prev = cacheRef.current;

    // When IDs change, the `subs` array changes (new reference from useMemo).
    // Invalidate the cache so we rebuild with the new subscription set.
    const subsChanged = prev === null || prev.subs !== subs;

    if (!subsChanged && prev) {
      // Fast path: check if any object snapshot changed.
      let dirty = false;
      for (const s of subs) {
        const current = s.getSnapshot();
        if (prev.snaps.get(s.id) !== current) {
          dirty = true;
          break;
        }
      }
      if (!dirty) {
        return prev.map;
      }
    }

    // Rebuild the Map and snapshot index.
    const newSnaps = new Map<string, CrmObject | undefined>();
    const newMap = new Map<string, CrmObject | undefined>();
    for (const s of subs) {
      const obj = s.getSnapshot();
      newSnaps.set(s.id, obj);
      newMap.set(s.id, obj);
    }
    cacheRef.current = { subs, map: newMap, snaps: newSnaps };
    return newMap;
  }, [subs]);

  const snapshotMap = useSyncExternalStore(subscribe, getSnapshot);

  // Preview projection — the plural mirror of `useCrmObjectSnapshot`. Ids in
  // this set can be entities that only exist in a proposal preview (a sibling
  // "create company" an association points at), which the store can never
  // hold. Without this, every consumer of the plural hook — most visibly
  // `EntityBadgeByIds`, which association rows render — resolves them to
  // `undefined` and falls back to "Unavailable".
  const preview = useActivePreviewHandle();

  const overlaySubscribe = useCallback(
    (cb: () => void) => {
      if (!preview || dedupedIds.length === 0) {
        return NOOP_UNSUBSCRIBE;
      }
      const unsubs = dedupedIds.map(id => preview.subscribeToOverlay(id, cb));
      return () => {
        for (const unsub of unsubs) {
          unsub();
        }
      };
    },
    [preview, dedupedIds]
  );
  // Value-compared snapshot: an unchanged overlay set yields an equal string,
  // so `useSyncExternalStore` sees no change and the map keeps its reference.
  const overlayGetSnapshot = useCallback(
    () =>
      preview
        ? JSON.stringify(dedupedIds.map(id => preview.getOverlay(id) ?? null))
        : '',
    [preview, dedupedIds]
  );
  const overlayVersion = useSyncExternalStore(
    overlaySubscribe,
    overlayGetSnapshot,
    overlayGetSnapshot
  );

  // Returns `snapshotMap` itself when no overlay applies, preserving the
  // reference stability the cache above works hard to provide.
  const projectedMap = useMemo(() => {
    if (!preview) {
      return snapshotMap;
    }
    let merged: Map<string, CrmObject | undefined> | null = null;
    for (const id of dedupedIds) {
      const overlay = preview.getOverlay(id);
      if (!overlay) {
        continue;
      }
      const projected = mergeProjection(
        snapshotMap.get(id),
        overlay,
        orgId ?? 0
      );
      if (projected) {
        merged ??= new Map(snapshotMap);
        merged.set(id, projected);
      }
    }
    return merged ?? snapshotMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- overlayVersion bumps recompute
  }, [preview, snapshotMap, dedupedIds, orgId, overlayVersion]);

  // Derive whether any entity in the set needs fetching. When clearAll()
  // empties the store (e.g., on org switch), all snapshot values flip to
  // undefined and hasIncomplete flips to true. This causes the effect below
  // to re-run and call ensureEntity() for each missing entity, re-fetching
  // them.
  //
  // Without this, the effect deps [orgId, dedupedIds] don't change after
  // a store clear, so the effect never re-fires and entity badges stay in
  // loading state forever. See useCrmObjectSnapshot for the single-entity
  // variant of the same fix.
  const hasIncomplete = useMemo(
    () =>
      dedupedIds.length > 0 &&
      dedupedIds.some(id => snapshotMap.get(id) === undefined),
    [dedupedIds, snapshotMap]
  );

  // Effect: queue any unfetched entities for batched fetch.
  useEffect(() => {
    if (!orgId || orgId <= 0) {
      return;
    }
    for (const id of dedupedIds) {
      // A `create` overlay's id has no server record — fetching it 404s and
      // marks the id failed, which renders as an error over an entity that
      // projected perfectly well.
      if (preview?.getOverlay(id)?.kind === 'create') {
        continue;
      }
      if (!hasCompleteObject(id)) {
        ensureEntity(orgId, id);
      }
    }
  }, [orgId, dedupedIds, hasIncomplete, preview]);

  return projectedMap;
}
